import { hideAll } from "tippy.js";

import { canv } from "@/canv/global.js";

import { setClick } from "@/canv/utils.js";
import { eventEmitter } from "@/eventEmitter";
import { ActiveVoteTool } from "@/tools/ActiveVoteTool";
import { arrowTool } from "@/tools/arrowTool/arrowTool.js";
import { CommentTool } from "@/tools/CommentTool/CommentTool";
import { cardTool } from "@/tools/cardTool/cardTool.js";
import { clipTool } from "@/tools/clipTool/clipTool.js";
import { CursorTool } from "@/tools/cursorTool";
import { dragTool } from "@/tools/dragTool/dragTool";
import { emoteTool } from "@/tools/emoteTool/emoteTool.js";
import { frameTool } from "@/tools/frameTool/frameTool.js";
import { mediaTool } from "@/tools/mediaTool/mediaTool.js";
import { mindMapTool } from "@/tools/mindMapTool/mindMapTool.js";
import { MoreStickersTool } from "@/tools/moreTool/MoreStickersTool";
import { moreTool } from "@/tools/moreTool/moreTool.js";
import { penTool } from "@/tools/penTool/penTool.js";
import { StickersTool } from "@/tools/StickersTool";
import { shapeTool } from "@/tools/shapeTool/shapeTool.js";
import { stickerTool } from "@/tools/stickerTool";
import { TemplatesTool } from "@/tools/TemplatesTool";
import { tableTool } from "@/tools/tableTool/tableTool.js";
import { textTool } from "@/tools/textTool/textTool.js";
import type { Tool, ToolName, Tools } from "@/tools/types";
import { EmbedTool } from "./embedTool/embedTool";
import { VoteTool } from "./VoteTool";

export class toolBox {
    constructor() {
        this.Tools = {
            cursorTool: new CursorTool(),
            stickerTool: new stickerTool(),
            textTool: new textTool(),
            shapeTool: new shapeTool(),
            penTool: new penTool(),
            arrowTool: new arrowTool(),
            mindMapTool: new mindMapTool(),
            frameTool: new frameTool(),
            emoteTool: new emoteTool(),
            mediaTool: new mediaTool(),
            moreTool: new moreTool(),
            tableTool: new tableTool(),
            cardTool: new cardTool(),
            dragTool: new dragTool(),
            templatesTool: new TemplatesTool(),
            stickersTool: new StickersTool(),
            moreStickerTool: new MoreStickersTool(),
            clipTool: new clipTool(),
            activeVoteTool: new ActiveVoteTool(),
            voteTool: new VoteTool(),
            commentTool: new CommentTool(),
            embedTool: new EmbedTool(),
        };

        this.currentTool = this.getFirstTool();
        eventEmitter.dispatch("toolbox.switch", { toolName: this.currentTool.name });
    }

    TryAction(type: string) {
        if (!this.currentTool) {
            return;
        }

        if (this.isDisabled && this.currentTool.name !== "dragTool") {
            return;
        }

        if ("TryAction" in this.currentTool) {
            this.currentTool.TryAction(type);
        }
    }

    drawTool() {
        if (this.currentTool != null) {
            this.currentTool.draw();
        }
    }

    getToolsNames(): ToolName[] {
        // showToolBox не вызывается для isBoardModeView()
        if (canv.isBoardModeEditMini()) {
            let tools: ToolName[] = [
                "cursorTool",
                "stickerTool",
                "textTool",
                "penTool",
                "commentTool",
                "emoteTool",
            ];

            if (canv.isGuest()) {
                tools = tools.filter((tool) => tool !== "commentTool");
            }

            return tools;
        }
        if (canv.isBoardModeViewCopy()) {
            return ["cursorTool"];
        }

        const height = window.innerHeight;
        const defaultToolsNames: ToolName[] = [
            "cursorTool",
            "templatesTool",
            "stickerTool",
            "textTool",
            "shapeTool",
            "arrowTool",
            "penTool",
            "frameTool",
        ];

        if (height > 768) {
            defaultToolsNames.push("mindMapTool", "commentTool");
        } else if (height > 744) {
            defaultToolsNames.push("mindMapTool");
        }
        defaultToolsNames.push("moreTool");

        return defaultToolsNames;
    }

    hide() {
        this.toolBox?.classList.add("toolBoxHidden");
    }

    show() {
        this.toolBox?.classList.remove("toolBoxHidden");
    }

    showToolBox() {
        if (this.isDisabled) {
            return;
        }

        const toolBox = document.createElement("div");
        toolBox.classList.add("toolBox");

        toolBox.innerHTML += this.getToolBoxTemplate();

        this.toolBox = toolBox;
        document.body.append(toolBox);

        const toolBoxWrapperContent = document.getElementsByClassName("toolBoxWrapperContent")[0];

        const toolsNames = this.getToolsNames();

        for (let i = 0; i < toolsNames.length; i++) {
            const tool = this.TryGetToolByName(toolsNames[i]);
            if (tool) {
                toolBoxWrapperContent.innerHTML += this.getToolItemTemplate(tool);
            }
        }

        this.init();
        if (this.currentTool) {
            this.currentTool.select();
        }
    }

    init() {
        this.initTools();
        setClick("toolBoxArrowUndo", canv.eventManager.Undo.bind(canv.eventManager));
        setClick("toolBoxArrowRedo", canv.eventManager.Redo.bind(canv.eventManager));
    }

    initTools() {
        const views = document.getElementsByClassName("toolBoxWrapperContentItem");

        for (let i = 0; i < views.length; i++) {
            const toolName = views[i].getAttribute("toolName") as ToolName | null;

            if (!toolName) continue;

            const tool = this.TryGetToolByName(toolName);

            if (tool == null) continue;

            tool.initView();
        }
    }

    getToolItemTemplate(tool: Tool) {
        return `<div data-hotkey="${tool.getToolHotKey()}" class="toolBoxWrapperContentItem ${tool.getToolClass()}" toolName=${tool.name} toolTipName="${tool.getToolTipName()}">${tool.getToolView()}</div>`;
    }

    getToolBoxArrowsTemplate() {
        if (canv.isBoardModeViewCopy()) {
            return "";
        }

        const actionSymbol = canv.actionManager.actionSymbol;

        return `
            <div class="toolBoxArrows">
                <div toolTipName="Отменить" class="toolBoxArrow toolBoxArrowUndo" data-hotkey="${actionSymbol} + Z">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 7V13H9" stroke="#2C2D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M21 17C21 14.6131 20.0518 12.3239 18.364 10.636C16.6761 8.94821 14.3869 8 12 8C9.78512 8.00226 7.64885 8.82116 6 10.3L3 13" stroke="#2C2D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
                <div toolTipName="Повторить" class="toolBoxArrow toolBoxArrowRedo" data-hotkey="${actionSymbol} + Shift + Z">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 7V13H15" stroke="#2C2D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M3 17C3 14.6131 3.94821 12.3239 5.63604 10.636C7.32387 8.94821 9.61305 8 12 8C14.2149 8.00226 16.3511 8.82116 18 10.3L21 13" stroke="#2C2D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
            </div>
        `;
    }

    getToolBoxTemplate() {
        return `
            <div class="toolBoxWrapper">
                <div class="toolBoxWrapperContent">
                </div>
            </div>
            ${this.getToolBoxArrowsTemplate()}
        `;
    }

    // Utils

    getCurrentTool() {
        return this.currentTool;
    }

    getFirstTool() {
        return this.Tools.cursorTool;
    }

    TryGetToolByName<T extends ToolName>(name: T) {
        return this.Tools[name];
    }

    switchTo(tool: Tool, isToggleMenu = true) {
        hideAll();
        if (!this.currentTool) {
            canv.redraw();
            return;
        }

        if (
            tool.name === this.currentTool.name &&
            this.currentTool.name === "penTool" &&
            isToggleMenu
        ) {
            (this.currentTool as penTool).reopenOrCloseSubmenu();
        } else {
            document.body.style.cursor = "default";
            this.currentTool.unselect();
            this.currentTool = tool;
            eventEmitter.dispatch("toolbox.switch", { toolName: this.currentTool.name });
            this.currentTool.select();
        }
        canv.redraw();
    }

    switchToByName(toolName: ToolName) {
        const tool = this.TryGetToolByName(toolName);
        if (!tool) {
            return;
        }

        this.switchTo(tool);
    }

    resetToolBox() {
        if (!this.currentTool) {
            return;
        }
        this.switchTo(this.currentTool);
    }

    switchToShapeTool(mode) {
        this.switchToByName("shapeTool");
        if (mode && (this.currentTool as shapeTool).subMenuShapes.includes(mode)) {
            (this.currentTool as shapeTool).setMode(mode);
        }
        canv.redraw();
    }

    Tools: Tools;
    currentTool: Tool | null = null;
    isDisabled = false;
    toolBox: HTMLElement | null = null;
}
